<script setup lang="ts">
import { computed } from 'vue'
import { VIcon } from '@/modules/shared/components'

type AlertType = 'info' | 'alert' | 'success' | 'error'

const props = defineProps<{
  type: AlertType
  icon?: string | null
}>()

const variants: Record<
  AlertType,
  { border: string; bg: string; text: string; iconColor: string; defaultIcon: string }
> = {
  info: {
    border: 'border-[#3b88af]',
    bg: 'bg-[#3b88af]/10',
    text: 'text-[#3b88af]',
    iconColor: 'text-[#3b88af]',
    defaultIcon: 'Help',
  },
  alert: {
    border: 'border-yellow-400',
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    iconColor: 'text-yellow-500',
    defaultIcon: 'QuestionCircleFilled',
  },
  success: {
    border: 'border-[#408F93]',
    bg: 'bg-[#408F93]/10',
    text: 'text-[#408F93]',
    iconColor: 'text-[#408F93]',
    defaultIcon: 'Check',
  },
  error: {
    border: 'border-red-500',
    bg: 'bg-red-50',
    text: 'text-red-700',
    iconColor: 'text-red-500',
    defaultIcon: 'SlashCircle01',
  },
}

const variant = computed(() => variants[props.type])

const containerClasses = computed(() => ['border-l-4', 'p-4', variant.value.bg, variant.value.border])

const iconName = computed(() => {
  if (props.icon === null) return null
  if (props.icon) return props.icon
  return variant.value.defaultIcon
})
</script>

<template>
  <div :class="containerClasses" role="alert">
    <div class="flex">
      <div v-if="iconName" class="shrink-0">
        <VIcon :name="iconName" class="h-5 w-5" :class="variant.iconColor" />
      </div>
      <div class="ml-3">
        <p class="text-sm" :class="variant.text">
          <slot />
        </p>
      </div>
    </div>
  </div>
</template>
